// The /v1/web/me client. Every figure the dashboard shows arrives here as the
// server sent it: cents plus a display string, never formatted in the browser.

export type Me = {
  username: string;
  bio: string;
  website: string;
  role: string;
  notify_1v1: boolean;
  budget_1v1: number;
  genres: string[];
  city: string;
  state: string;
  country: string;
  instagram: string;
  soundcloud: string;
  twitter: string;
  cash_app: string;
  logo_url: string;
  member_since?: string;
  email: string;
  paypal_email: string;
  email_verified: boolean;
  balance_cents: number;
  balance_display: string;
  cosign_credits_cents: number;
  cosign_credits_display: string;
  rank?: number;
  contests_won: number;
  contests_entered: number;
  desktop_key?: string;
  genre_options: string[];
  budget_options: number[];
};

// The fields PATCH /v1/web/me accepts.
export const EDITABLE = [
  "username",
  "bio",
  "website",
  "role",
  "notify_1v1",
  "budget_1v1",
  "genres",
  "city",
  "state",
  "country",
  "instagram",
  "soundcloud",
  "twitter",
  "cash_app",
  "paypal_email",
] as const;
export type EditableField = (typeof EDITABLE)[number];
export type ProfilePatch = Partial<Pick<Me, EditableField>>;

export type InboxKind = "system" | "contest" | "payout" | "fan";
export type InboxMessage = {
  id: string;
  kind: InboxKind;
  from: string;
  subject: string;
  preview: string;
  body: string;
  is_read: boolean;
  created_at: string;
  ctas: { label: string; href: string }[];
};
export type Inbox = { unread_count: number; messages: InboxMessage[] };

export type WithdrawalItem = {
  id: string;
  amount_cents: number;
  amount_display: string;
  destination: string;
  state: string;
  requested_at: string;
  paid_at?: string;
};
export type Withdrawals = {
  available_cents: number;
  available_display: string;
  pending_cents: number;
  pending_display: string;
  minimum_cents: number;
  minimum_display: string;
  items: WithdrawalItem[];
};

// What a producer earned: backing share still accruing on live markets, each
// settled contest's paid lines, and lifetime totals. Every figure is the
// server's; nothing here is derived.
export type EarningsLive = {
  contest_slug: string;
  contest_title: string;
  status: string;
  earned_cents: number;
  earned_display: string;
  backers: number;
};
export type EarningsContest = {
  contest_slug: string;
  contest_title: string;
  backing_share_cents: number;
  backing_share_display: string;
  winner_cut_cents: number;
  winner_cut_display: string;
  total_cents: number;
  total_display: string;
  paid_at: string;
};
export type EarningsTotals = {
  backing_share_cents: number;
  backing_share_display: string;
  winner_cut_cents: number;
  winner_cut_display: string;
  total_cents: number;
  total_display: string;
};
export type Earnings = { live: EarningsLive[]; contests: EarningsContest[]; totals: EarningsTotals };

// A failed call carries the server's message and, for a 400, the field it
// named. status 0 is a network failure.
export type Failure = { ok: false; status: number; error: string; field?: string };
export type Result<T> = { ok: true; data: T } | Failure;

// Credit bundles the checkout sells; Stripe prices them on the server.
export const CREDIT_BUNDLES = [25, 50, 100];

const str = (v: unknown) => (typeof v === "string" ? v : "");
const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const strs = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);

export function normalizeMe(raw: unknown): Me | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  if (typeof b.username !== "string" || typeof b.balance_display !== "string") return null;
  return {
    username: b.username,
    bio: str(b.bio),
    website: str(b.website),
    role: str(b.role),
    notify_1v1: b.notify_1v1 === true,
    budget_1v1: num(b.budget_1v1),
    genres: strs(b.genres),
    city: str(b.city),
    state: str(b.state),
    country: str(b.country),
    instagram: str(b.instagram),
    soundcloud: str(b.soundcloud),
    twitter: str(b.twitter),
    cash_app: str(b.cash_app),
    logo_url: /^https?:\/\//.test(str(b.logo_url)) ? str(b.logo_url) : "",
    member_since: typeof b.member_since === "string" ? b.member_since : undefined,
    email: str(b.email),
    paypal_email: str(b.paypal_email),
    email_verified: b.email_verified === true,
    balance_cents: num(b.balance_cents),
    balance_display: b.balance_display,
    cosign_credits_cents: num(b.cosign_credits_cents),
    cosign_credits_display: str(b.cosign_credits_display),
    rank: typeof b.rank === "number" && b.rank > 0 ? b.rank : undefined,
    contests_won: num(b.contests_won),
    contests_entered: num(b.contests_entered),
    desktop_key: typeof b.desktop_key === "string" && b.desktop_key !== "" ? b.desktop_key : undefined,
    genre_options: strs(b.genre_options),
    budget_options: Array.isArray(b.budget_options) ? b.budget_options.filter((x): x is number => typeof x === "number") : [],
  };
}

const KINDS: readonly InboxKind[] = ["system", "contest", "payout", "fan"];

export function normalizeInbox(raw: unknown): Inbox | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  if (!Array.isArray(b.messages)) return null;
  const messages = b.messages
    .filter((m): m is Record<string, unknown> => !!m && typeof m === "object" && typeof m.id === "string")
    .map((m) => ({
      id: m.id as string,
      kind: KINDS.includes(m.kind as InboxKind) ? (m.kind as InboxKind) : "system",
      from: str(m.from),
      subject: str(m.subject),
      preview: str(m.preview),
      body: str(m.body),
      is_read: m.is_read === true,
      created_at: str(m.created_at),
      ctas: (Array.isArray(m.ctas) ? m.ctas : [])
        .filter((c): c is { label: string; href: string } => !!c && typeof c.label === "string" && typeof c.href === "string")
        .filter((c) => c.href.startsWith("/") || c.href.startsWith("https://")),
    }));
  return { unread_count: num(b.unread_count), messages };
}

export function normalizeWithdrawals(raw: unknown): Withdrawals | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  if (!Array.isArray(b.items) || typeof b.available_display !== "string") return null;
  return {
    available_cents: num(b.available_cents),
    available_display: b.available_display,
    pending_cents: num(b.pending_cents),
    pending_display: str(b.pending_display),
    minimum_cents: num(b.minimum_cents),
    minimum_display: str(b.minimum_display),
    items: b.items
      .filter((i): i is Record<string, unknown> => !!i && typeof i === "object" && typeof i.id === "string")
      .map((i) => ({
        id: i.id as string,
        amount_cents: num(i.amount_cents),
        amount_display: str(i.amount_display),
        destination: str(i.destination),
        state: str(i.state),
        requested_at: str(i.requested_at),
        paid_at: typeof i.paid_at === "string" ? i.paid_at : undefined,
      })),
  };
}

export function normalizeEarnings(raw: unknown): Earnings | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  if (!Array.isArray(b.live) || !Array.isArray(b.contests) || !b.totals || typeof b.totals !== "object") return null;
  const t = b.totals as Record<string, unknown>;
  return {
    live: b.live
      .filter((l): l is Record<string, unknown> => !!l && typeof l === "object")
      .filter((l) => typeof l.earned_display === "string" && l.earned_display !== "" && num(l.backers) >= 1)
      .map((l) => ({
        contest_slug: str(l.contest_slug),
        contest_title: str(l.contest_title),
        status: str(l.status),
        earned_cents: num(l.earned_cents),
        earned_display: l.earned_display as string,
        backers: num(l.backers),
      })),
    contests: b.contests
      .filter((c): c is Record<string, unknown> => !!c && typeof c === "object")
      .filter((c) => typeof c.total_display === "string" && c.total_display !== "")
      .map((c) => ({
        contest_slug: str(c.contest_slug),
        contest_title: str(c.contest_title),
        backing_share_cents: num(c.backing_share_cents),
        backing_share_display: str(c.backing_share_display),
        winner_cut_cents: num(c.winner_cut_cents),
        winner_cut_display: str(c.winner_cut_display),
        total_cents: num(c.total_cents),
        total_display: c.total_display as string,
        paid_at: str(c.paid_at),
      })),
    totals: {
      backing_share_cents: num(t.backing_share_cents),
      backing_share_display: str(t.backing_share_display),
      winner_cut_cents: num(t.winner_cut_cents),
      winner_cut_display: str(t.winner_cut_display),
      total_cents: num(t.total_cents),
      total_display: str(t.total_display),
    },
  };
}

export async function call<T>(
  method: string,
  url: string,
  body: unknown,
  normalize: (raw: unknown) => T | null,
  fallback: string,
): Promise<Result<T>> {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: body === undefined ? { Accept: "application/json" } : { "Content-Type": "application/json", Accept: "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    return { ok: false, status: 0, error: fallback };
  }
  const raw = (await res.json().catch(() => null)) as unknown;
  if (res.ok) {
    const data = normalize(raw);
    if (data !== null) return { ok: true, data };
    return { ok: false, status: res.status, error: fallback };
  }
  const b = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    ok: false,
    status: res.status,
    error: typeof b.error === "string" && b.error !== "" ? b.error : fallback,
    field: typeof b.field === "string" && b.field !== "" ? b.field : undefined,
  };
}

const okBody = (raw: unknown) => (raw && typeof raw === "object" ? true : null);
const urlBody = (raw: unknown) => {
  const u = raw && typeof raw === "object" ? (raw as { url?: unknown }).url : undefined;
  return typeof u === "string" && /^https:\/\//.test(u) ? u : null;
};
const presignBody = (raw: unknown) => {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as { upload_url?: unknown; key?: unknown; headers?: unknown };
  if (typeof b.upload_url !== "string" || !/^https:\/\//.test(b.upload_url) || typeof b.key !== "string") return null;
  const headers: Record<string, string> = {};
  if (b.headers && typeof b.headers === "object") {
    for (const [k, v] of Object.entries(b.headers as Record<string, unknown>)) if (typeof v === "string") headers[k] = v;
  }
  return { upload_url: b.upload_url, key: b.key, headers };
};
const withdrawalBody = (raw: unknown) =>
  raw && typeof raw === "object" && typeof (raw as { id?: unknown }).id === "string" ? (raw as WithdrawalItem) : null;

const FAILED = "Something went wrong. Try again in a moment.";

export const api = {
  me: () => call("GET", "/v1/web/me", undefined, normalizeMe, FAILED),
  updateMe: (patch: ProfilePatch) => call("PATCH", "/v1/web/me", patch, normalizeMe, FAILED),
  presignAvatar: (contentType: string, size: number) =>
    call("POST", "/v1/web/me/avatar/presign", { content_type: contentType, size }, presignBody, FAILED),
  confirmAvatar: (key: string) => call("POST", "/v1/web/me/avatar/confirm", { key }, normalizeMe, FAILED),
  deleteAvatar: () => call("DELETE", "/v1/web/me/avatar", undefined, normalizeMe, FAILED),
  inbox: () => call("GET", "/v1/web/me/inbox", undefined, normalizeInbox, FAILED),
  setRead: (id: string, read: boolean) =>
    call("POST", `/v1/web/me/inbox/${encodeURIComponent(id)}/read`, { read }, okBody, FAILED),
  readAll: () => call("POST", "/v1/web/me/inbox/read-all", {}, okBody, FAILED),
  withdrawals: () => call("GET", "/v1/web/me/withdrawals", undefined, normalizeWithdrawals, FAILED),
  earnings: () => call("GET", "/v1/web/me/earnings", undefined, normalizeEarnings, FAILED),
  requestWithdrawal: (amountCents: number, paypalEmail: string, idempotencyKey: string) =>
    call(
      "POST",
      "/v1/web/me/withdrawals",
      { amount_cents: amountCents, paypal_email: paypalEmail, idempotency_key: idempotencyKey },
      withdrawalBody,
      FAILED,
    ),
  checkout: (bundle: number) => call("POST", "/v1/web/me/credits/checkout", { bundle }, urlBody, FAILED),
};

// uploadToS3 PUTs the file to a presigned URL with the headers the signature
// covers. No credentials: the URL is the authorization.
export async function uploadToS3(url: string, file: Blob, headers: Record<string, string>): Promise<boolean> {
  try {
    const res = await fetch(url, { method: "PUT", body: file, headers });
    return res.ok;
  } catch {
    return false;
  }
}

// newIdempotencyKey names one withdrawal attempt; a retry reuses it.
export function newIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `wd-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
